import { useEffect, useState, type CSSProperties } from "react";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useSession } from "@pontimed/auth-client";
import { cn } from "@pontimed/ui";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, MailCheck, type LucideIcon } from "lucide-react";

import { useAuthModal } from "@/components/auth/auth-modal-context";
import {
  peekPendingOAuthLink,
  rememberPendingOAuthLink,
} from "@/components/auth/pending-oauth-link";
import { Button, buttonVariants } from "@/components/ui/button";

interface ErrorSearch {
  /** Standard Auth.js / OAuth error code (e.g. AccessDenied, OAuthCallback). */
  error?: string;
  /** Optional path to retry from. */
  next?: string;
  /** Parked Facebook link token, with `error=oauth_link_requires_email_code`. */
  link?: string;
}

/** apps/api auth-oauth.ts OAUTH_LINK_REQUIRES_EMAIL_CODE. */
const LINK_REQUIRES_EMAIL_CODE = "oauth_link_requires_email_code";

/** apps/api auth-oauth.ts OAUTH_LINK_REQUIRES_STEP_UP. */
const LINK_REQUIRES_STEP_UP = "oauth_link_requires_step_up";

/**
 * Where to land after the email-code sign-in. A path only: the modal hands
 * `returnTo` to `router.navigate({ to })`, which does not parse a query
 * string out of `to`. The completion hook opens the Accounts tab itself when
 * it finishes on an /auth/* page.
 */
const AFTER_SIGN_IN_PATH = "/account/user/settings";

export const Route = createFileRoute("/auth/error")({
  validateSearch: (search: Record<string, unknown>): ErrorSearch => ({
    error: typeof search.error === "string" ? search.error : undefined,
    next: typeof search.next === "string" ? search.next : undefined,
    link: typeof search.link === "string" ? search.link : undefined,
  }),
  component: AuthErrorPage,
});

function AuthErrorPage() {
  const { error, next, link } = Route.useSearch();
  const { t } = useLingui();

  if (error === LINK_REQUIRES_EMAIL_CODE) {
    return <PendingLinkNotice linkToken={link} next={next} />;
  }
  if (error === LINK_REQUIRES_STEP_UP) {
    return <StepUpLinkNotice />;
  }

  const message = describeAuthError(error, t);

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center px-4 py-10">
      <StatusBackdrop />
      <div className={STATUS_CARD} style={STATUS_CARD_STYLE}>
        <div className="flex flex-col items-center gap-4 text-center">
          <StatusTile icon={AlertTriangle} tone="destructive" />
          <h1 className={STATUS_TITLE} style={STAGGER_1}>
            <Trans>Sign-in failed</Trans>
          </h1>
          <p className={STATUS_BODY} style={STAGGER_2}>
            {message}
          </p>
          {error && (
            <p className="bg-muted text-muted-foreground m-0 max-w-full rounded-md px-2 py-1 font-mono text-[11px] tracking-[0.12em] break-all uppercase">
              {error}
            </p>
          )}
          <div className="mt-2 flex w-full flex-col gap-3">
            <Button
              className={BTN_PRIMARY}
              onClick={() => {
                if (typeof window === "undefined") return;
                const params = new URLSearchParams({ auth: "open" });
                if (next) params.set("next", next);
                window.location.assign(`/find?${params.toString()}`);
              }}
            >
              <Trans>Try again</Trans>
            </Button>
            <Link to="/" className={cn(buttonVariants({ variant: "outline" }), BTN_SECONDARY)}>
              <Trans>Back to homepage</Trans>
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * A Facebook sign-in whose email belongs to an existing PontiMed account
 * (apps/api auth-oauth.ts): Facebook's email proves nothing about the mailbox,
 * so nothing was linked and nobody was signed in. The person signs in to that
 * account with an email code (prefilled); under that session
 * `PendingOAuthLinkPrompt` (AuthModalProvider) asks whether to connect the
 * Facebook account and completes the parked link only on "Connect" — whether
 * the code is typed in the modal or the magic link opens in a new tab.
 *
 * Without a `link` token (the account already has an account of that provider
 * connected, or the link could not be parked) only the sign-in is offered. That
 * one comes from Google and Apple too, so it does not name Facebook.
 */
function PendingLinkNotice({ linkToken, next }: { linkToken?: string; next?: string }) {
  const navigate = useNavigate();
  const auth = useAuthModal();
  // The token is held here: the URL drops it right away (history, shared
  // screenshots) — it is useless without this browser's cookie, but there is
  // no reason to keep it on screen.
  const [token] = useState(linkToken);
  const [email, setEmail] = useState<string | null>(null);
  const [peeking, setPeeking] = useState(Boolean(linkToken));

  useEffect(() => {
    if (!token) return;
    rememberPendingOAuthLink(token);
    void navigate({
      to: "/auth/error",
      search: { error: LINK_REQUIRES_EMAIL_CODE, next },
      replace: true,
    });
    let cancelled = false;
    void peekPendingOAuthLink(token).then((pending) => {
      if (cancelled) return;
      setEmail(pending?.email ?? null);
      setPeeking(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token, navigate, next]);

  const canConnect = Boolean(token);

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center px-4 py-10">
      <StatusBackdrop />
      <div className={STATUS_CARD} style={STATUS_CARD_STYLE}>
        <div className="flex flex-col items-center gap-4 text-center">
          <StatusTile icon={MailCheck} tone="primary" />
          <h1 className={STATUS_TITLE} style={STAGGER_1}>
            <Trans>Confirm it's your account</Trans>
          </h1>
          <p className={STATUS_BODY} style={STAGGER_2}>
            {canConnect ? (
              <Trans>
                This email already has a PontiMed account. Sign in with a code sent to it to connect
                Facebook.
              </Trans>
            ) : (
              <Trans>
                This email already has a PontiMed account. Sign in with a code sent to it instead.
              </Trans>
            )}
          </p>
          {email && (
            <p className="border-border bg-muted text-foreground m-0 max-w-full truncate rounded-lg border px-3 py-1.5 font-mono text-[13px] font-medium">
              {email}
            </p>
          )}
          <div className="mt-2 flex w-full flex-col gap-3">
            <Button
              className={BTN_PRIMARY}
              disabled={peeking}
              onClick={() =>
                auth.open({
                  prefillEmail: email ?? undefined,
                  returnTo: canConnect ? AFTER_SIGN_IN_PATH : (next ?? "/account/user/dashboard"),
                })
              }
            >
              <Trans>Sign in with an email code</Trans>
            </Button>
            <Link to="/" className={cn(buttonVariants({ variant: "outline" }), BTN_SECONDARY)}>
              <Trans>Back to homepage</Trans>
            </Link>
          </div>
          {canConnect && (
            <p className="text-muted-foreground m-0 text-xs leading-relaxed">
              <Trans>
                For your security, Facebook is connected only after you confirm the email.
              </Trans>
            </p>
          )}
        </div>
      </div>
    </main>
  );
}

/**
 * A Google / Apple / Facebook sign-in whose email belongs to an account with
 * two-factor authentication (apps/api auth-oauth.ts): the sign-in cannot add
 * itself to that account, so "Try again" would only fail again. The person
 * signs in another way and connects the provider in Settings → Accounts, where
 * the session and a recent 2FA factor are what allows it.
 *
 * The modal stays on this page after the sign-in (no `returnTo`, which could
 * not carry `?tab=accounts` anyway); the session flipping sends them on.
 */
function StepUpLinkNotice() {
  const navigate = useNavigate();
  const auth = useAuthModal();
  const session = useSession();

  useEffect(() => {
    if (session.status !== "authenticated") return;
    void navigate({
      to: "/account/user/settings",
      search: { tab: "accounts" } as never,
      replace: true,
    });
  }, [session.status, navigate]);

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center px-4 py-10">
      <StatusBackdrop />
      <div className={STATUS_CARD} style={STATUS_CARD_STYLE}>
        <div className="flex flex-col items-center gap-4 text-center">
          <StatusTile icon={MailCheck} tone="primary" />
          <h1 className={STATUS_TITLE} style={STAGGER_1}>
            <Trans>Confirm it's your account</Trans>
          </h1>
          <p className={STATUS_BODY} style={STAGGER_2}>
            <Trans>
              This email already has a PontiMed account, protected by two-factor authentication.
              Sign in with a code sent to it, then connect this sign-in method in Settings →
              Accounts.
            </Trans>
          </p>
          <div className="mt-2 flex w-full flex-col gap-3">
            <Button className={BTN_PRIMARY} onClick={() => auth.open()}>
              <Trans>Sign in with an email code</Trans>
            </Button>
            <Link to="/" className={cn(buttonVariants({ variant: "outline" }), BTN_SECONDARY)}>
              <Trans>Back to homepage</Trans>
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

/* ─── Puls status screen ───
   Same language as the apps/web Puls kit (`components/puls/primitives.tsx`,
   not importable from apps/app): blueprint grid + soft glow behind one
   centred card, state icon in a toned tile, display heading, 48px buttons.
   Presentational only. */

const GRID_LINE = "color-mix(in oklch, var(--primary) 8%, transparent)";
const GRID_MASK = "radial-gradient(ellipse 70% 60% at 50% 40%, black 15%, transparent 72%)";
const GRID_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(${GRID_LINE} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: "44px 44px",
  maskImage: GRID_MASK,
  WebkitMaskImage: GRID_MASK,
};
const GLOW_STYLE: CSSProperties = {
  backgroundImage:
    "radial-gradient(closest-side, color-mix(in oklch, var(--primary) 16%, transparent), color-mix(in oklch, var(--accent) 7%, transparent) 55%, transparent)",
};
/** Soft primary wash at the top of the card. */
const STATUS_CARD_STYLE: CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, color-mix(in oklch, var(--primary-soft) 55%, var(--card)) 0%, var(--card) 45%)",
};
const STATUS_CARD =
  "border-border bg-card w-full max-w-md rounded-2xl border p-6 shadow-[0_30px_70px_-45px_color-mix(in_oklch,var(--primary)_60%,transparent)] sm:p-8";
const STATUS_TITLE =
  "puls-reveal font-display text-foreground m-0 text-2xl leading-tight font-semibold tracking-[-0.025em] text-balance";
const STATUS_BODY = "puls-reveal text-muted-foreground m-0 text-sm leading-relaxed";
/** `.puls-reveal` stagger steps (80 ms each, see puls.css). */
const STAGGER_1 = { "--puls-i": 1 } as CSSProperties;
const STAGGER_2 = { "--puls-i": 2 } as CSSProperties;
const BTN = "h-12 w-full gap-2 rounded-lg px-6 text-sm font-semibold";
const BTN_PRIMARY = `${BTN} hover:bg-primary-hover`;
const BTN_SECONDARY = `${BTN} bg-card text-foreground hover:border-primary/40 hover:bg-card hover:text-primary`;

type StatusTone = "primary" | "destructive";

const TILE_TONE: Record<StatusTone, string> = {
  primary: "border-primary/20 bg-primary-soft text-primary",
  destructive: "border-destructive-border bg-destructive-soft text-destructive",
};

function StatusBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0" style={GRID_STYLE} />
      <div
        className="absolute top-[10%] left-1/2 h-[70%] w-[min(92%,44rem)] -translate-x-1/2 blur-[20px]"
        style={GLOW_STYLE}
      />
    </div>
  );
}

function StatusTile({ icon: Icon, tone }: { icon: LucideIcon; tone: StatusTone }) {
  return (
    <span
      aria-hidden="true"
      className={`puls-reveal grid size-14 shrink-0 place-items-center rounded-2xl border ${TILE_TONE[tone]}`}
    >
      <Icon className="size-6" />
    </span>
  );
}

/**
 * Maps standard OAuth + Auth.js error codes to user-readable strings. Falls
 * back to a generic message for unknown codes (the raw code stays visible
 * below as a debugging aid for support requests).
 */
function describeAuthError(code: string | undefined, t: ReturnType<typeof useLingui>["t"]): string {
  if (!code) return t(msg`Something went wrong while signing you in. Please try again.`);
  switch (code) {
    case "AccessDenied":
      return t(msg`Access denied. The provider didn't grant the required permissions.`);
    case "Verification":
      return t(msg`This sign-in link is no longer valid. Request a new one and try again.`);
    case "OAuthCallback":
    case "OAuthCallbackError":
      return t(msg`The provider returned an unexpected response. Please try again.`);
    case "OAuthSignin":
      return t(msg`Couldn't reach the provider. Check your connection and try again.`);
    case "EmailSignin":
      return t(msg`Couldn't send your sign-in email. Try a different method or contact support.`);
    case "Configuration":
      return t(msg`Sign-in is temporarily unavailable. Please try again in a few minutes.`);
    case "CredentialsSignin":
      return t(msg`Invalid credentials. Check your details and try again.`);
    case "SessionRequired":
      return t(msg`This page requires a sign-in. Continue to start.`);
    default:
      return t(msg`Something went wrong while signing you in. Please try again.`);
  }
}
